/* src/pages/VerifyEmail.jsx
 * Email verification page — apply oobCode in-app, then onboarding
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reload, applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { useToast } from '../contexts/ToastContext';
import { authApi } from '../utils/api';
import '../styles/auth.css';

function VerifyShell({ children }) {
  return (
    <div className="auth-page">
      <div className="auth-wrap">
        <div className="auth-card">{children}</div>
      </div>
    </div>
  );
}

export default function VerifyEmail() {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const handledOobRef = useRef(null);

  const oobCode = searchParams.get('oobCode');

  // Handle email verification link (oobCode — no mode required)
  useEffect(() => {
    if (!oobCode || handledOobRef.current === oobCode) return;
    handledOobRef.current = oobCode;
    handleEmailVerificationLink(oobCode);
  }, [oobCode]);

  // If coming from legacy Firebase continueUrl (?verified=true)
  useEffect(() => {
    if (searchParams.get('verified') === 'true' && currentUser && !oobCode) {
      checkVerification();
    }
  }, [searchParams, currentUser, oobCode]);

  async function handleEmailVerificationLink(code) {
    try {
      setVerifying(true);
      setError(null);
      setChecking(false);

      await checkActionCode(auth, code);
      await applyActionCode(auth, code);

      if (currentUser) {
        await reload(currentUser);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const freshUser = auth.currentUser;
        if (freshUser) {
          await reload(freshUser);
        }
      }

      setVerified(true);
      setVerifying(false);

      sessionStorage.removeItem('selectedPlan');
      window.history.replaceState({}, document.title, '/verify-email');

      setTimeout(() => {
        if (auth.currentUser) {
          navigate('/onboarding');
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (err) {
      console.error('Error verifying email:', err);
      setError(
        'This verification link is invalid or has expired. Please request a new verification email.'
      );
      setVerifying(false);
      setChecking(false);
    }
  }

  // Waiting room: require login unless we're processing / recovering from an oobCode
  useEffect(() => {
    if (oobCode || verified || verifying) return;
    if (!currentUser) {
      navigate('/signup');
      return;
    }

    checkVerification();

    const interval = setInterval(() => {
      if (!verified && currentUser) {
        checkVerification();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentUser, verified, verifying, oobCode]);

  async function checkVerification() {
    if (!currentUser) return;

    try {
      await reload(currentUser);
      const freshUser = auth.currentUser;

      if (freshUser && freshUser.emailVerified) {
        setVerified(true);
        setChecking(false);
        sessionStorage.removeItem('selectedPlan');
        setTimeout(() => {
          navigate('/onboarding');
        }, 1500);
      } else {
        setChecking(false);
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      setChecking(false);
    }
  }

  async function handleResendVerification() {
    if (!currentUser) return;

    try {
      setResending(true);
      setError(null);
      await authApi.sendVerificationEmail();
      showToast('Verification email sent! Please check your inbox.');
    } catch (err) {
      console.error('Error resending verification:', err);
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  }

  if (verifying) {
    return (
      <VerifyShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Verifying</span>
        <h1 className="auth-title">Verifying your email</h1>
        <p className="auth-subtitle" style={{ marginBottom: '1.5rem' }}>
          Please wait while we verify your email address.
        </p>
        <p className="auth-footer" style={{ marginTop: 0 }}>
          <Link to="/login" className="auth-link">Back to login</Link>
        </p>
      </VerifyShell>
    );
  }

  if (verified) {
    return (
      <VerifyShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Verified</span>
        <h1 className="auth-title" style={{ color: 'var(--accent-ink)' }}>Email verified ✓</h1>
        <div className="auth-success" style={{ marginBottom: '1rem' }}>
          Redirecting you to complete your setup...
        </div>
      </VerifyShell>
    );
  }

  // Expired/invalid link while logged out — offer login to resend
  if (error && !currentUser) {
    return (
      <VerifyShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Link expired</span>
        <h1 className="auth-title">Verification link expired</h1>
        <div className="auth-error" style={{ marginBottom: '1.5rem' }}>{error}</div>
        <p className="auth-subtitle" style={{ marginBottom: '1.5rem' }}>
          Log in to request a new verification email.
        </p>
        <Link to="/login" className="inbox-pill-btn-primary" style={{ display: 'inline-flex', justifyContent: 'center', width: '100%' }}>
          Log in to resend
        </Link>
      </VerifyShell>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <VerifyShell>
      <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Verification required</span>
      <h1 className="auth-title">Verify your email</h1>
      <p className="auth-subtitle" style={{ lineHeight: 1.6 }}>
        We&apos;ve sent a verification email to <strong>{currentUser.email}</strong>
      </p>
      <p style={{ fontSize: '0.95rem', color: 'var(--dim)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
        Please check your inbox and click the verification link to continue.
        After verification, you&apos;ll be taken straight to setting up your account.
      </p>

      {error && (
        <div className="auth-error">{error}</div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={handleResendVerification}
          disabled={resending}
          className="inbox-pill-btn-primary"
          style={{ width: '100%', justifyContent: 'center', opacity: resending ? 0.6 : 1 }}
        >
          {resending ? 'Sending...' : 'Resend Verification Email'}
        </button>
      </div>

      <p className="auth-footer" style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
        <Link to="/login" className="auth-link">Back to login</Link>
      </p>

      {checking && (
        <p style={{ fontSize: '0.9rem', color: 'var(--dim)', marginBottom: '1.5rem' }}>
          Checking verification status...
        </p>
      )}

      <div className="fb-callout info" style={{ textAlign: 'left' }}>
        <p style={{ margin: 0 }}>
          <strong>Next steps:</strong>
        </p>
        <ol style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', marginBottom: 0 }}>
          <li>Check your email inbox</li>
          <li>Click the verification link</li>
          <li>Set up your first form and start your 14-day free trial</li>
        </ol>
      </div>
    </VerifyShell>
  );
}
